import { classNames } from "@/utils/classNames"
import React, { useEffect, useRef, useState } from "react"

export interface CircleProgressProps {
  value: number
  maxValue: number

  size?: number
  strokeWidth?: number

  isCountdown?: boolean
  timeRemaining?: number
  onTimerComplete?: () => void
  onTimerTick?: (remainingTime: number) => void

  showValue?: boolean
  showPercentage?: boolean
  centerContent?: React.ReactNode

  animationDuration?: number
  disableAnimation?: boolean

  className?: string
  variant?: "default" | "success" | "warning" | "error" | "info"

  "aria-label"?: string
  "aria-describedby"?: string
}

const colorVariants = {
  default: "stroke-yellow1",
  success: "stroke-green-500",
  warning: "stroke-warning",
  error: "stroke-red1",
  info: "stroke-blue-500",
}

const formatTime = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  if (hours > 0) {
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
  }
  return `${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`
}

export const CircleProgress: React.FC<CircleProgressProps> = ({
  value,
  maxValue,
  size = 80,
  strokeWidth = 8,
  isCountdown = false,
  timeRemaining,
  onTimerComplete,
  onTimerTick,
  showValue = true,
  showPercentage = false,
  centerContent,
  animationDuration = 300,
  disableAnimation = false,
  className,
  variant = "default",
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
}) => {
  const [currentTime, setCurrentTime] = useState(timeRemaining || 0)
  const intervalRef = useRef<ReturnType<typeof setInterval>>(undefined)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
      setPrefersReducedMotion(mediaQuery.matches)

      const handleChange = (e: MediaQueryListEvent) => {
        setPrefersReducedMotion(e.matches)
      }

      mediaQuery.addEventListener("change", handleChange)
      return () => mediaQuery.removeEventListener("change", handleChange)
    }
  }, [])

  useEffect(() => {
    if (isCountdown && currentTime > 0) {
      intervalRef.current = setInterval(() => {
        setCurrentTime((prev) => {
          const newTime = prev - 1
          onTimerTick?.(newTime)
          if (newTime <= 0) {
            onTimerComplete?.()
          }
          return Math.max(0, newTime)
        })
      }, 1000)
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [isCountdown, currentTime, onTimerTick, onTimerComplete])

  useEffect(() => {
    if (timeRemaining !== undefined) {
      setCurrentTime(timeRemaining)
    }
  }, [timeRemaining])

  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const progressPercentage = isCountdown
    ? currentTime / (timeRemaining || 1)
    : value / maxValue
  const strokeDashoffset =
    circumference * (1 - Math.max(0, Math.min(1, progressPercentage)))

  const shouldAnimate = !disableAnimation && !prefersReducedMotion
  const displayValue = isCountdown ? currentTime : value
  const percentage = Math.round(progressPercentage * 100)

  return (
    <div
      className={classNames(
        "relative inline-flex items-center justify-center",
        className,
      )}
      role="progressbar"
      aria-valuenow={isCountdown ? currentTime : value}
      aria-valuemin={0}
      aria-valuemax={isCountdown ? timeRemaining || 1 : maxValue}
      aria-label={
        ariaLabel ||
        (isCountdown
          ? `Timer: ${formatTime(currentTime)}`
          : `Progress: ${percentage}%`)
      }
      aria-describedby={ariaDescribedBy}
    >
      <svg
        width={size}
        height={size}
        className="-rotate-90 transform"
        aria-hidden="true"
      >
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          className={classNames(
            "fill-transparent transition-all",
            shouldAnimate && `duration-${animationDuration}`,
            colorVariants[variant],
          )}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={shouldAnimate ? {} : { transition: "none" }}
        />
      </svg>

      <div className="absolute inset-0 flex items-center justify-center">
        {centerContent || (
          <div className="text-center">
            {showValue && (
              <div
                className={classNames(
                  "select-none font-bold",
                  isCountdown
                    ? "text-xl leading-tight text-white"
                    : "text-sm font-medium text-grey1 dark:text-white",
                )}
              >
                {isCountdown ? formatTime(currentTime) : displayValue}
              </div>
            )}
            {showPercentage && (
              <div className="select-none text-xs text-grey5">
                {percentage}%
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
